// ---------------------------------------------------------------------------
// The bot as player 2 — a search bot that plays a live war from your browser
// ---------------------------------------------------------------------------
//
// A bot war is an ordinary war whose player 2 is BOT_UID. Nobody else runs
// its turns, so the human's own client does: useBotOpponent (mounted with the
// war board) watches the game and, once the bot's round is open (the previous
// round's result saved, its round counter not yet moved), builds player 2's
// view, asks the search bot (strong preset: it predicts the human with the
// fast search bot half the time, so it expects a defended corridor; a
// fraction of a second per round, which a live war doesn't notice) for
// orders and writes the
// submission exactly as a second player's client would. The human's client
// resolves and saves every round, as either client may.
//
// The bot decides only from what player 2 could see: its own side, and the
// human's side as it stood at the start of the round. When the human has
// already submitted, the database holds their submitted state, so the bot
// reads their previous board (the same rule the hook applies to a human
// opponent) and takes this round's rocket purchases back off their Grid —
// the human's submit records them in p1RocketsBought.

import { useEffect, useRef } from 'react'
import { ref, get, update } from 'firebase/database'
import { db } from '../FirebaseConfig'
import { ARMIES_PER_ROUND, STRUCTURES_TO_WIN } from '../rules/gameConfig'
import { buildRoundActionsLedger } from '../rules/gameLogic'
import { submitOrders } from '../rules/submitOrders'
import { playerView, rememberRound } from '../rules/bots/view'
import { searchBot, PRESETS } from '../rules/bots/searchBot'
import { seededRng } from '../rules/bots/rng'
import VALUE_MODEL from '../rules/bots/valueModel'
import { CURRENT_BOT, botByUid, botLabel } from './roster'
import { generateHomefrontV3 } from '../rules/homefrontAutogen'

// The live bot: the strong search, predicting its opponent with the player
// model. Wars created before the rename carry the old id.
// Who the bot is (its uid, name and version) comes from the roster
// (bot/roster.js; docs/BOTS.md): the live bot is its last entry, and every
// earlier bot's uid still marks its wars as bot wars.
export const BOT_UID = CURRENT_BOT.uid
export const isBotUid = (uid) => !!botByUid(uid)
export const BOT_NAME = botLabel(CURRENT_BOT)
export const isBotWar = (game) => isBotUid(game?.player2)

const parse = (v) => (typeof v === 'string' ? JSON.parse(v) : v)

// A Grid for the bot: the auto-generator's.
export function botHomefront(rng = Math.random) {
    return generateHomefrontV3(rng)
}

// Whether the bot owes a submission for its current round.
export function botRoundOpen(game) {
    if (!isBotWar(game) || game.endedAt != null) return false
    if (!game.p1HomefrontReady || !game.p2HomefrontReady) return false
    if ((game.p1StructuresLost ?? 0) >= STRUCTURES_TO_WIN || (game.p2StructuresLost ?? 0) >= STRUCTURES_TO_WIN) return false
    const r = game.p2CurrentRound ?? 1
    if ((game.p1CurrentRound ?? 1) < r) return false
    return r === 1 || game.attackSubmittedForRound === r
}

const hash = (s) => { let h = 2166136261; for (const c of String(s)) h = Math.imul(h ^ c.charCodeAt(0), 16777619); return h >>> 0 }

// The human's Grid as it stood when the round opened: with the rockets they
// bought in a submit already made this round taken back off.
function humanGridAtRoundStart(game, humanAhead) {
    const hf = parse(game.p1Homefront)
    if (!humanAhead || !hf) return hf
    const bought = new Set(parse(game.p1RocketsBought ?? '[]') ?? [])
    if (!bought.size) return hf
    const out = JSON.parse(JSON.stringify(hf))
    for (const st of Object.values(out.structures)) {
        const keep = (st.rocketCells || []).filter(s => !bought.has(s))
        st.rockets = Math.max(0, (st.rockets || 0) - ((st.rocketCells || []).length - keep.length))
        st.rocketCells = keep
    }
    return out
}

// Player 2's view for its open round: what the bot decides from, and the
// memory to save with its submission. Its memory (the cells it has seen hold
// the human's broods) is kept on the game doc, botMemory, so it survives a
// reload.
export function botView(game) {
    const r = game.p2CurrentRound ?? 1
    const humanAhead = (game.p1CurrentRound ?? 1) > r
    const p1 = {
        board: parse(humanAhead ? game.p1LastRound : game.p1State),
        homefront: humanGridAtRoundStart(game, humanAhead),
        yield: 0,
        structuresLost: game.p1StructuresLost ?? 0,
    }
    const p2 = {
        board: parse(game.p2State),
        homefront: parse(game.p2Homefront),
        yield: game.p2Yield ?? 0,
        structuresLost: game.p2StructuresLost ?? 0,
    }
    // resolveRound's round is the counter after both submits (round 1 → 2).
    const memory = parse(game.botMemory ?? null) ?? {}
    const view = playerView({ round: r + 1, capacities: game.warCapacities ?? null, p1, p2 }, 'p2', memory)
    return { view, p2, memory: rememberRound(view) }
}

// The bot's submission for its open round, as the Firebase update a
// player-2 client would write.
export function botSubmission(game) {
    const r = game.p2CurrentRound ?? 1
    const { view, p2, memory } = botView(game)
    // Strong preset, predicting the human with the player model fitted from
    // their own archived wars (rules/bots/playerModel.js, refit with
    // scripts/fit-player-model.js), defending against what they can legally
    // strike with, plus two worst-case futures (rules/bots/threats.js): 61%
    // head to head against the same bot without them (400 wars). Its fresh
    // drones cover its broods under construction, and each plan also faces
    // its own worst case at a fifth of its score, so plans that guard their
    // broods win out: 59% against the bot without either (400 wars). Plans
    // are scored by the learned win probability (rules/bots/valueNet.js,
    // trained by scripts/train-value.py) rather than the hand-written
    // evaluation: 54% head to head against it, 98.9% vs the brooder (400
    // wars each).
    const orders = searchBot(view, null, seededRng(hash(`${game.id}:${r}`)), {
        ...PRESETS.strong, opponentModel: 'player', threatAware: true, worstCase: 2, planWorstCase: 1, planWorstWeight: 0.2,
        valueModel: VALUE_MODEL,
    })
    const submitted = submitOrders(p2, orders)
    const updates = {
        p2LastRound: JSON.stringify(p2.board),
        p2State: JSON.stringify(submitted.board),
        p2CurrentRound: r + 1,
        p2AttackResults: false,
        p2ArmiesToDeploy: ARMIES_PER_ROUND,
        p2TargetPlots: JSON.stringify(submitted.targetPlots),
        p2TargetSubplots: JSON.stringify(submitted.targetSubplots),
        p2DropZones: JSON.stringify(submitted.dropZones),
        p2DroneStrikes: JSON.stringify(submitted.droneStrikes),
        p2BaseBuilds: JSON.stringify(submitted.baseBuilds),
        p2RoundActions: JSON.stringify(buildRoundActionsLedger(orders.board)),
        p2RocketsBought: JSON.stringify(submitted.rocketsStationed),
        botMemory: JSON.stringify(memory),
    }
    if (submitted.rocketsStationed.length) updates.p2Homefront = JSON.stringify(submitted.homefront)
    if (submitted.spent > 0) updates.p2Yield = submitted.yield
    return updates
}

// Plays the bot's turns in a bot war. Mount it with the war board, passing
// `hold` while the human still has a round to reveal: the bot moves on only
// once they have watched it, as a human opponent would. (Submitting during
// their reveal made the war board cut the reveal short — the opponent a round
// ahead mid-animation — so the bot's drones jumped to their next positions.)
// A turn,
// once scheduled, is not cancelled by game updates (they arrive constantly) —
// only by the board unmounting, which also forgets the round it was for, so
// a remount (React's StrictMode mounts every component twice in development)
// schedules it again.
export function useBotOpponent(game, { delayMs = 700, hold = false } = {}) {
    const busy = useRef(null)
    const timers = useRef(new Set())
    useEffect(() => () => {
        timers.current.forEach(clearTimeout)
        timers.current.clear()
        busy.current = null
    }, [])
    useEffect(() => {
        if (hold || !botRoundOpen(game)) return
        const round = game.p2CurrentRound ?? 1
        if (busy.current === round) return
        busy.current = round
        const id = game.id
        const t = setTimeout(async () => {
            timers.current.delete(t)
            try {
                const gameRef = ref(db, `games/${id}`)
                const snap = await get(gameRef)
                if (!snap.exists()) return
                const fresh = snap.val()
                if (!botRoundOpen(fresh) || (fresh.p2CurrentRound ?? 1) !== round) return
                await update(gameRef, botSubmission(fresh))
            } catch (e) {
                console.error('The bot could not play its round:', e)
                busy.current = null
            }
        }, delayMs)
        timers.current.add(t)
    }, [game, delayMs, hold])
}
